import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nearbyAreas, resolveArea } from '../neighbourhoodApi';
import { CouldBuyFormState, NamedArea } from '../types';
import { LatLng } from '../../../../utils/types';
import { CheckboxRow, PriceHistogram } from './criteriaControls';
import { AMENITY_OPTIONS, FLOOR_OPTIONS, RENOVATION_OPTIONS } from './criteriaOptions';
import { BuildingChipIcon, ChipRemoveIcon, DistrictChipIcon, PinChipIcon } from '../locationIcons';
import { formatPriceRange } from '../../../../ListingCard/cardUtils';

interface MainStepProps {
  form: CouldBuyFormState;
  buildingLabel: string;
  // Whether an anchor building exists at all. Separate from buildingLabel: a
  // building we have never stored has no address to name it with, and the
  // 500 m circle around it is still a real, offerable choice (JAA-328).
  hasAnchor: boolean;
  // The anchor building's coordinates, used to pre-surface nearby district chips.
  coordinates: LatLng | null;
  onChange: (updates: Partial<CouldBuyFormState>) => void;
  onAddArea: (area: NamedArea) => void;
  onRemoveArea: (index: number) => void;
  onToggleCircle: (on: boolean) => void;
  onOpenMap: () => void;
  onPublish: () => void;
  isPublishing: boolean;
  // Inline submit error to show above the CTA; null hides the banner.
  // True when editing an existing buy listing (JAA-304): that submit path is
  // building-only (PUT /off-market-willingness/:id has no area-interest
  // representation) and the building is the listing's identity. So the
  // area/circle expansion controls are hidden AND the anchor chip loses its
  // remove affordance — changing the building is a move, a separate feature,
  // not an edit.
  lockedToBuilding?: boolean;
  // The flow is editing something that already exists, so the CTA saves rather
  // than posts. One flag for both kinds of buy interest: they are the same thing
  // at different widths, and only `lockedToBuilding` cares which.
  isEdit?: boolean;
}

// How many nearby-district suggestion chips to surface on the Location card.
const MAX_SUGGESTED_AREAS = 3;

type SectionKey =
  | 'location'
  | 'description'
  | 'size'
  | 'rooms'
  | 'price'
  | 'floor'
  | 'renovation'
  | 'amenities';

const ROOM_OPTIONS = ['1', '2', '3', '4', '5+'];

// Keep only digits so the size/price fields stay numeric for the JAA-277 criteria payload.
const digitsOnly = (value: string) => value.replace(/[^\d]/g, '');

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M6.75 2.25 1.5 4.5v11.25l5.25-2.25 4.5 2.25 5.25-2.25V2.25L11.25 4.5 6.75 2.25Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M6.75 2.25v11.25M11.25 4.5v11.25" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

interface CriteriaCardProps {
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  // Optional icon shown before the summary value (the building icon on Location).
  summaryIcon?: React.ReactNode;
}

// One expand/collapse section card from the Figma "Option accordion" (node
// 3611:93887). Closed: a compact row with a grey title and a dark value summary
// on the right. Open: the title grows to the bold 24px headline, the summary is
// dropped, and the control slot renders below. The parent owns a single open
// key — a classic one-at-a-time accordion.
const CriteriaCard = ({ title, summary, open, onToggle, children, summaryIcon }: CriteriaCardProps) => (
  <div className={`cbf-card${open ? ' cbf-card--open' : ''}`}>
    <button type="button" className="cbf-card__header" onClick={onToggle} aria-expanded={open}>
      <span className="cbf-card__title">{title}</span>
      <span className="cbf-card__summary">
        {summaryIcon}
        <span className="cbf-card__summary-text">{summary}</span>
      </span>
    </button>
    {/* Always rendered so the reveal can animate both open and closed (a
        conditionally-mounted body cannot animate its exit). */}
    <div className="cbf-card__reveal">
      <div className="cbf-card__body">{children}</div>
    </div>
  </div>
);

// The single-screen "Create a buy interest" form (Figma node 3592:90002,
// accordion 3612:78475). A stack of expand/collapse cards — Location (area
// pickers + map), Description, Size, Rooms, Price — above the pinned
// "Post your interest" CTA. Single-open: expanding a card collapses the rest.
const MainStep = ({
  form,
  buildingLabel,
  hasAnchor,
  coordinates,
  onChange,
  onAddArea,
  onRemoveArea,
  onToggleCircle,
  onOpenMap,
  onPublish,
  isPublishing,
  lockedToBuilding,
  isEdit = false,
}: MainStepProps) => {
  const { t } = useTranslation();
  // Location starts open; every other section starts collapsed. Single-open
  // accordion: at most one section is expanded at a time.
  const [openSection, setOpenSection] = useState<SectionKey | null>('location');
  // Progressive disclosure: surface only the building's own (smallest) district
  // first; the nearby ones stay hidden until the buyer picks it, then animate in.
  const [ownArea, setOwnArea] = useState<NamedArea | null>(null);
  const [nearbySuggestions, setNearbySuggestions] = useState<NamedArea[]>([]);
  const [nearbyRevealed, setNearbyRevealed] = useState(false);

  // Resolve the anchor's own district + a few neighbours once, for the chips.
  // Clear any prior building's suggestions up front so swapping anchors never
  // leaves stale chips on screen during (or after a rejected) lookup; `live`
  // keeps only the latest coordinates' response from repopulating.
  useEffect(() => {
    if (!coordinates) return undefined;
    let live = true;
    setOwnArea(null);
    setNearbySuggestions([]);
    setNearbyRevealed(false);
    (async () => {
      try {
        const [own, near] = await Promise.all([
          resolveArea(coordinates.lat, coordinates.lng),
          nearbyAreas(coordinates.lat, coordinates.lng, MAX_SUGGESTED_AREAS),
        ]);
        if (!live) return;
        setOwnArea(own ?? null);
        // Dedupe nearby by name so the building's own district never repeats below.
        const seen = new Set<string>();
        if (own) seen.add(own.name.toLowerCase());
        const nearList: NamedArea[] = [];
        near.forEach((area) => {
          const key = (area.name || '').toLowerCase();
          if (!key || seen.has(key)) return;
          seen.add(key);
          nearList.push(area);
        });
        setNearbySuggestions(nearList.slice(0, MAX_SUGGESTED_AREAS));
      } catch {
        // Lookup failed — leave the chips cleared rather than showing stale ones.
      }
    })();
    return () => {
      live = false;
    };
  }, [coordinates]);

  // Reveal the nearby suggestions once the buyer has picked their own district,
  // and keep them revealed even if they later remove it.
  const ownAreaKey = ownArea ? ownArea.ext_id || ownArea.name : null;
  const ownAreaSelected =
    ownAreaKey !== null && form.areas.some((s) => (s.ext_id || s.name) === ownAreaKey);
  useEffect(() => {
    if (ownAreaSelected) setNearbyRevealed(true);
  }, [ownAreaSelected]);

  // The anchor's own district fully covers the 500 m circle, so keeping both is
  // redundant — deselect the circle whenever that district is picked.
  const onToggleCircleRef = useRef(onToggleCircle);
  onToggleCircleRef.current = onToggleCircle;
  useEffect(() => {
    if (ownAreaSelected && form.circleSelected) onToggleCircleRef.current(false);
  }, [ownAreaSelected, form.circleSelected]);

  // Whether this flow opened from a real building. The Activity FAB starts a buy
  // interest with no seed, so there is nothing to anchor a 500 m circle on and
  // nothing to expand outwards from — Location then offers only "Choose on map".
  // (form.anchorSelected defaults to true, so it cannot answer this on its own.)

  const isOpen = (key: SectionKey) => openSection === key;

  // Open this section (collapsing whichever was open), or close it if it was
  // the open one.
  const toggleSection = (key: SectionKey) =>
    setOpenSection((prev) => (prev === key ? null : key));

  // Summarise each selection for its collapsed-row value column. The Location row
  // reflects the ACTUAL selections — the anchor building only while its chip is
  // selected, plus the 500 m circle and any districts — with an icon for the first.
  const locationItems: { label: string; icon: React.ReactNode }[] = [];
  if (form.anchorSelected && buildingLabel) {
    locationItems.push({ label: buildingLabel, icon: <BuildingChipIcon color="currentColor" /> });
  }
  if (hasAnchor && form.circleSelected) {
    locationItems.push({
      label: t('newLandingPage.couldBuy.main.within500m'),
      icon: <PinChipIcon color="currentColor" />,
    });
  }
  form.areas.forEach((area) => {
    locationItems.push({ label: area.name, icon: <DistrictChipIcon color="currentColor" /> });
  });
  const locationSummary = locationItems.length
    ? locationItems.length > 1
      ? `${locationItems[0].label} +${locationItems.length - 1}`
      : locationItems[0].label
    : t('newLandingPage.couldBuy.main.chooseLocation');
  const locationSummaryIcon = locationItems[0]?.icon ?? null;
  const descriptionValue = form.description.trim()
    ? form.description.trim()
    : t('newLandingPage.couldBuy.main.anyDescription');
  const sizeValue =
    form.sizeMin || form.sizeMax
      ? [form.sizeMin ? `${form.sizeMin} m²` : '', form.sizeMax ? `${form.sizeMax} m²` : '']
          .filter(Boolean)
          .join(' – ')
      : t('newLandingPage.couldBuy.main.anySize');
  const roomsValue = form.roomsMin
    ? `${form.roomsMin} ${t('newLandingPage.couldBuy.main.roomsUnit')}`
    : t('newLandingPage.couldBuy.main.anyRooms');
  const priceValue =
    form.priceMin || form.priceMax
      ? formatPriceRange(Number(form.priceMin) || undefined, Number(form.priceMax) || undefined)
      : t('newLandingPage.couldBuy.main.anyPrice');
  const floorOption = FLOOR_OPTIONS.find((o) => o.value === form.floor);
  const floorValue = floorOption
    ? t(`newLandingPage.couldBuy.main.${floorOption.key}`)
    : t('newLandingPage.couldBuy.main.anyFloor');
  const renovationOption = RENOVATION_OPTIONS.find((o) => o.value === form.renovation);
  const renovationValue = renovationOption
    ? t(`newLandingPage.couldBuy.main.${renovationOption.key}`)
    : t('newLandingPage.couldBuy.main.anyRenovation');
  const amenitiesValue = form.amenities.length
    ? AMENITY_OPTIONS.filter((o) => form.amenities.includes(o.value))
        .map((o) => t(`newLandingPage.couldBuy.main.${o.key}`))
        .join(', ')
    : t('newLandingPage.couldBuy.main.anyAmenities');

  // Add or remove an amenity key from the multi-select set.
  const toggleAmenity = (value: string) =>
    onChange({
      amenities: form.amenities.includes(value)
        ? form.amenities.filter((a) => a !== value)
        : [...form.amenities, value],
    });

  return (
    <div className="cbf-step cbf-step--main">
      <div className="cbf-step__body cbf-step__body--main">

        {/* Location (Figma 3612:78393) */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.locationTitle')}
          summary={locationSummary}
          summaryIcon={locationSummaryIcon}
          open={isOpen('location')}
          onToggle={() => toggleSection('location')}
        >
          {/* Selected green chips (each removable): anchor building, the 500 m
              circle, and named areas. */}
          <div className="cbf-main__selected-chips">
            {form.anchorSelected && buildingLabel && lockedToBuilding ? (
              <span className="cbf-chip cbf-chip--selected">
                <span className="cbf-chip__icon" aria-hidden="true">
                  <BuildingChipIcon />
                </span>
                <span className="cbf-chip__label">{buildingLabel}</span>
              </span>
            ) : null}
            {form.anchorSelected && buildingLabel && !lockedToBuilding ? (
              <button
                type="button"
                className="cbf-chip cbf-chip--selected"
                onClick={() => onChange({ anchorSelected: false, circleSelected: false })}
                aria-label={t('newLandingPage.couldBuy.location.removeChipAria')}
              >
                <span className="cbf-chip__icon" aria-hidden="true">
                  <BuildingChipIcon />
                </span>
                <span className="cbf-chip__label">{buildingLabel}</span>
                <span className="cbf-chip__remove" aria-hidden="true">
                  <ChipRemoveIcon />
                </span>
              </button>
            ) : null}
            {hasAnchor && form.circleSelected ? (
              <button
                type="button"
                className="cbf-chip cbf-chip--selected"
                onClick={() => onToggleCircle(false)}
                aria-label={t('newLandingPage.couldBuy.location.removeChipAria')}
              >
                <span className="cbf-chip__icon" aria-hidden="true">
                  <PinChipIcon color="#fff" />
                </span>
                <span className="cbf-chip__label">
                  {t('newLandingPage.couldBuy.main.within500m')}
                </span>
                <span className="cbf-chip__remove" aria-hidden="true">
                  <ChipRemoveIcon />
                </span>
              </button>
            ) : null}
            {form.areas.map((area, index) => (
              <button
                key={area.ext_id || area.name}
                type="button"
                className="cbf-chip cbf-chip--selected"
                onClick={() => onRemoveArea(index)}
                aria-label={t('newLandingPage.couldBuy.location.removeChipAria')}
              >
                <span className="cbf-chip__icon" aria-hidden="true">
                  <DistrictChipIcon color="#fff" />
                </span>
                <span className="cbf-chip__label">{area.name}</span>
                <span className="cbf-chip__remove" aria-hidden="true">
                  <ChipRemoveIcon />
                </span>
              </button>
            ))}
          </div>

          {/* Expand your interest area — hidden while editing a buy listing,
              whose submit path can't carry an area/circle (JAA-304). */}
          {!lockedToBuilding && (
            <>
          {/* Expand your interest area — only meaningful when there is an
              anchor to expand outwards from. */}
          {hasAnchor ? (
            <p className="cbf-main__expand-label">
              {t('newLandingPage.couldBuy.main.expandArea')}
            </p>
          ) : null}

          {/* Suggestion chips: the 500 m circle (when off), nearby districts
              pre-surfaced as one-tap chips, then the map picker. */}
          <div className="cbf-main__suggestion-chips">
            {/* Building unselected: offer it back as a one-tap chip. */}
            {!form.anchorSelected && buildingLabel ? (
              <button
                type="button"
                className="cbf-chip"
                onClick={() => onChange({ anchorSelected: true })}
              >
                <span className="cbf-chip__icon" aria-hidden="true">
                  <BuildingChipIcon />
                </span>
                <span className="cbf-chip__label">{buildingLabel}</span>
              </button>
            ) : null}
            {/* The 500 m circle is anchored on the building, so only offer it
                while the building is selected. */}
            {hasAnchor && form.anchorSelected && !form.circleSelected && !ownAreaSelected ? (
              <button type="button" className="cbf-chip" onClick={() => onToggleCircle(true)}>
                <span className="cbf-chip__icon" aria-hidden="true">
                  <PinChipIcon />
                </span>
                <span className="cbf-chip__label">
                  {t('newLandingPage.couldBuy.main.within500m')}
                </span>
              </button>
            ) : null}
            {ownArea && !ownAreaSelected ? (
              <button
                type="button"
                className="cbf-chip"
                onClick={() => {
                  if (form.circleSelected) onToggleCircle(false);
                  onAddArea(ownArea);
                }}
              >
                <span className="cbf-chip__icon" aria-hidden="true">
                  <DistrictChipIcon />
                </span>
                <span className="cbf-chip__label">{ownArea.name}</span>
              </button>
            ) : null}
            {nearbyRevealed
              ? nearbySuggestions
                  .filter(
                    (a) =>
                      !form.areas.some((s) => (s.ext_id || s.name) === (a.ext_id || a.name)),
                  )
                  .map((area, index) => (
                    <button
                      key={area.ext_id || area.name}
                      type="button"
                      className="cbf-chip cbf-chip--reveal"
                      style={{ animationDelay: `${index * 60}ms` }}
                      onClick={() => onAddArea(area)}
                    >
                      <span className="cbf-chip__icon" aria-hidden="true">
                        <DistrictChipIcon />
                      </span>
                      <span className="cbf-chip__label">{area.name}</span>
                    </button>
                  ))
              : null}
            <button type="button" className="cbf-chip" onClick={onOpenMap}>
              <span className="cbf-chip__icon cbf-chip__icon--map" aria-hidden="true">
                <MapPinIcon />
              </span>
              <span className="cbf-chip__label">
                {t('newLandingPage.couldBuy.main.chooseOnMap')}
              </span>
            </button>
          </div>
            </>
          )}
        </CriteriaCard>

        {/* Description */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.descriptionLabel')}
          summary={descriptionValue}
          open={isOpen('description')}
          onToggle={() => toggleSection('description')}
        >
          <textarea
            className="cbf-criteria-input cbf-criteria-input--textarea"
            value={form.description}
            placeholder={t('newLandingPage.couldBuy.main.descriptionPlaceholder')}
            rows={3}
            maxLength={1100}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </CriteriaCard>

        {/* Size */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.sizeLabel')}
          summary={sizeValue}
          open={isOpen('size')}
          onToggle={() => toggleSection('size')}
        >
          <div className="cbf-range-row">
            <span className="cbf-range-box">
              <input
                className="cbf-range-box__input"
                type="text"
                inputMode="numeric"
                value={form.sizeMin}
                placeholder={t('newLandingPage.couldBuy.main.minPlaceholder')}
                onChange={(e) => onChange({ sizeMin: digitsOnly(e.target.value) })}
              />
              <span className="cbf-range-box__unit">m²</span>
            </span>
            <span className="cbf-range-row__sep">–</span>
            <span className="cbf-range-box">
              <input
                className="cbf-range-box__input"
                type="text"
                inputMode="numeric"
                value={form.sizeMax}
                placeholder={t('newLandingPage.couldBuy.main.maxPlaceholder')}
                onChange={(e) => onChange({ sizeMax: digitsOnly(e.target.value) })}
              />
              <span className="cbf-range-box__unit">m²</span>
            </span>
          </div>
        </CriteriaCard>

        {/* Rooms */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.roomsLabel')}
          summary={roomsValue}
          open={isOpen('rooms')}
          onToggle={() => toggleSection('rooms')}
        >
          <div className="cbf-tabs">
            <button
              type="button"
              className={`cbf-tab${!form.roomsMin ? ' cbf-tab--selected' : ''}`}
              onClick={() => onChange({ roomsMin: '' })}
            >
              {t('newLandingPage.couldBuy.main.anyTab')}
            </button>
            {ROOM_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`cbf-tab${form.roomsMin === option ? ' cbf-tab--selected' : ''}`}
                onClick={() => onChange({ roomsMin: option })}
              >
                {option}
              </button>
            ))}
          </div>
          <CheckboxRow
            checked={form.roomsFlexible}
            label={t('newLandingPage.couldBuy.main.roomsFlexible')}
            onToggle={() => onChange({ roomsFlexible: !form.roomsFlexible })}
          />
        </CriteriaCard>

        {/* Price range */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.priceLabel')}
          summary={priceValue}
          open={isOpen('price')}
          onToggle={() => toggleSection('price')}
        >
          <PriceHistogram
            min={form.priceMin}
            max={form.priceMax}
            onChange={(updates) => onChange(updates)}
          />
          <div className="cbf-range-row">
            <span className="cbf-range-box">
              <input
                className="cbf-range-box__input"
                type="text"
                inputMode="numeric"
                value={form.priceMin}
                placeholder={t('newLandingPage.couldBuy.main.minPlaceholder')}
                onChange={(e) => onChange({ priceMin: digitsOnly(e.target.value) })}
              />
              <span className="cbf-range-box__unit">€</span>
            </span>
            <span className="cbf-range-row__sep">–</span>
            <span className="cbf-range-box">
              <input
                className="cbf-range-box__input"
                type="text"
                inputMode="numeric"
                value={form.priceMax}
                placeholder={t('newLandingPage.couldBuy.main.maxPlaceholder')}
                onChange={(e) => onChange({ priceMax: digitsOnly(e.target.value) })}
              />
              <span className="cbf-range-box__unit">€</span>
            </span>
          </div>
        </CriteriaCard>

        {/* Floor (Figma 3612:78397) */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.floorLabel')}
          summary={floorValue}
          open={isOpen('floor')}
          onToggle={() => toggleSection('floor')}
        >
          <div className="cbf-tabs">
            <button
              type="button"
              className={`cbf-tab${!form.floor ? ' cbf-tab--selected' : ''}`}
              onClick={() => onChange({ floor: '' })}
            >
              {t('newLandingPage.couldBuy.main.anyFloor')}
            </button>
            {FLOOR_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`cbf-tab${form.floor === option.value ? ' cbf-tab--selected' : ''}`}
                onClick={() => onChange({ floor: option.value })}
              >
                {t(`newLandingPage.couldBuy.main.${option.key}`)}
              </button>
            ))}
          </div>
        </CriteriaCard>

        {/* Renovation status (Figma 3612:78398) */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.renovationLabel')}
          summary={renovationValue}
          open={isOpen('renovation')}
          onToggle={() => toggleSection('renovation')}
        >
          <div className="cbf-tabs">
            <button
              type="button"
              className={`cbf-tab${!form.renovation ? ' cbf-tab--selected' : ''}`}
              onClick={() => onChange({ renovation: '' })}
            >
              {t('newLandingPage.couldBuy.main.anyRenovation')}
            </button>
            {RENOVATION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`cbf-tab${form.renovation === option.value ? ' cbf-tab--selected' : ''}`}
                onClick={() => onChange({ renovation: option.value })}
              >
                {t(`newLandingPage.couldBuy.main.${option.key}`)}
              </button>
            ))}
          </div>
        </CriteriaCard>

        {/* Amenities (Figma 3612:78399) */}
        <CriteriaCard
          title={t('newLandingPage.couldBuy.main.amenitiesLabel')}
          summary={amenitiesValue}
          open={isOpen('amenities')}
          onToggle={() => toggleSection('amenities')}
        >
          <div className="cbf-amenities">
            {AMENITY_OPTIONS.map((option) => (
              <CheckboxRow
                key={option.value}
                checked={form.amenities.includes(option.value)}
                label={t(`newLandingPage.couldBuy.main.${option.key}`)}
                onToggle={() => toggleAmenity(option.value)}
              />
            ))}
          </div>
        </CriteriaCard>
      </div>

      {/* Floating CTA — gradient fade + yellow pill */}
      <div className="cbf-main__post-wrap">
        <button
          type="button"
          className="cbf-cta cbf-cta--post"
          onClick={onPublish}
          disabled={isPublishing}
        >
          {isPublishing
            ? t('common.loading')
            : t(
                isEdit
                  ? 'newLandingPage.couldBuy.main.saveButton'
                  : 'newLandingPage.couldBuy.main.postButton',
              )}
        </button>
      </div>
    </div>
  );
};

export default MainStep;
